import { Link } from "react-router-dom";
import { ArrowRight, Mail, Lock, Phone, Clock } from "lucide-react";

const openEstimate = () => window.dispatchEvent(new Event("open-estimate-modal"));

const COLS = [
  {
    title: "Services",
    items: [
      { label: "Get Estimate", onClick: openEstimate },
      { label: "Medications", to: "/#meds" },
      { label: "How It Works", to: "/#how" },
    ],
  },
  {
    title: "Company",
    items: [
      { label: "About Us", to: "/about" },
      { label: "Contact", to: "/contact" },
      { label: "Blog", to: "/blog" },
    ],
  },
  {
    title: "Resources",
    items: [
      { label: "How It Works", to: "/#how" },
      { label: "FAQ", to: "/#faq" },
    ],
  },
  {
    title: "Account",
    items: [
      { label: "Log In", to: "/login" },
      { label: "Register", to: "/register" },
      { label: "My Orders", to: "/dashboard" },
    ],
  },
  {
    title: "Legal",
    items: [
      { label: "Privacy Policy", to: "/privacy" },
      { label: "Terms of Service", to: "/terms" },
      { label: "SMS Terms", to: "/sms-terms" },
    ],
  },
];

export default function Footer() {
  return (
    <footer data-testid="footer" className="bg-paw-navy text-white">
      <div className="border-b border-white/10">
        <div className="max-w-[1180px] mx-auto px-6 xl:px-10 py-10 flex flex-col sm:flex-row items-center justify-between gap-6 text-center sm:text-left">
          <p className="text-lg font-semibold">Start saving on your pet's medication today.</p>
          <button
            onClick={openEstimate}
            data-testid="footer-cta-btn"
            className="bg-paw-primary hover:bg-paw-primary-hover text-white rounded-full px-6 py-3 text-sm font-semibold transition-all inline-flex items-center gap-2 shadow-[0_10px_24px_-10px_rgba(15,98,214,0.7)]"
          >
            Get Instant Estimate <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="max-w-[1180px] mx-auto px-6 xl:px-10 py-14">
        <div className="grid grid-cols-2 md:grid-cols-12 gap-8 md:gap-10">
          <div className="col-span-2 md:col-span-4">
            <Link to="/" className="inline-flex items-center">
              <img src="/pawlinx-logo-footer.png" alt="PawLinx" className="h-9 sm:h-10 w-auto" />
            </Link>
            <p className="text-sm text-white/55 leading-relaxed max-w-xs mt-4">
              Affordable, vet verified pet medication. In partnership with licensed Canadian pharmacies and veterinarians, delivered across the United States. PawLinx is a referral service, not a pharmacy.
            </p>
            <a href="mailto:info@pawlinx.com" className="flex w-fit items-center gap-2 text-sm text-white/55 hover:text-white transition-colors mt-4" data-testid="footer-email">
              <Mail className="w-4 h-4 text-paw-gold shrink-0" aria-hidden="true" /> info@pawlinx.com
            </a>
            <a href="tel:+18888272717" className="flex w-fit items-center gap-2 text-sm text-white/85 hover:text-white transition-colors mt-2" data-testid="footer-phone">
              <Phone className="w-4 h-4 text-paw-gold shrink-0" aria-hidden="true" />
              <span className="font-semibold">1-888-827-2717</span>
              <span className="text-white/70">Toll Free</span>
            </a>
            <p className="flex items-start gap-2 text-sm text-white/70 mt-2" data-testid="footer-hours">
              <Clock className="w-4 h-4 text-paw-gold shrink-0 mt-0.5" aria-hidden="true" />
              <span>Mon-Fri: 8AM to 6PM, Sat: 8AM to 3PM PST</span>
            </p>
            <p className="text-sm text-white/55 mt-3" data-testid="footer-canadian">
              Proudly a Canadian company
            </p>
            <div className="inline-flex items-center gap-2 bg-white/10 rounded-full pl-3 pr-3.5 py-1.5 mt-4" data-testid="footer-stripe-badge">
              <Lock className="w-3.5 h-3.5 text-white/70" />
              <span className="text-xs text-white/70">Secured by</span>
              <img src="/stripe-logo.svg" alt="Stripe" className="h-4 w-auto brightness-0 invert opacity-80" />
            </div>
          </div>

          {COLS.map((col) => (
            <div key={col.title} className="md:col-span-2">
              <p className="text-xs font-semibold tracking-widest uppercase text-white/30 mb-4">{col.title}</p>
              <ul className="space-y-2.5">
                {col.items.map((it) => (
                  <li key={it.label}>
                    {it.onClick ? (
                      <button onClick={it.onClick} className="text-sm text-white/55 hover:text-white transition-colors text-left">{it.label}</button>
                    ) : (
                      <Link to={it.to} className="text-sm text-white/55 hover:text-white transition-colors">{it.label}</Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-[1180px] mx-auto px-6 xl:px-10 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-white/30">&copy; {new Date().getFullYear()} PawLinx. A referral service, not a pharmacy. All rights reserved.</p>
          <a
            href="https://www.averoan.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-white/25 hover:text-white/45 transition-colors"
            data-testid="powered-by-averoan"
          >
            Powered by Averoan Technologies Inc.
          </a>
        </div>
      </div>
    </footer>
  );
}
